/**
 * NOTAS FISCAIS — registrar, nunca emitir (D4).
 *
 * Ninguém digita os 44 números da chave. A nota entra por um destes caminhos:
 *   📷 câmera no código de barras da DANFE ou no QR do cupom (sem sinal também);
 *   📄 o arquivo XML (o mais completo) ou o PDF da nota;
 *   🖼️ uma foto ou print (nada automático: valor e data à mão).
 * Onde o celular não lê código pela câmera (o iPhone, por exemplo), o app diz
 * isso e oferece o XML, o PDF ou a foto — nunca trava.
 *
 * Cada nota é ENTRADA ou SAÍDA e se liga à ficha, à despesa do extrato ou ao
 * movimento da catira (venda ou recria). Daí sai o relatório de receitas e
 * despesas (P3), que lê `notasDoPeriodo` (src/dominio/notas.ts).
 *
 * Quem pode o quê (o banco confere de novo, migração 009):
 *  - gestor: registra e anula qualquer nota;
 *  - comprador: registra nas fichas e nas despesas dele, vê só essas e anula a
 *    que ele registrou;
 *  - Consulta: vê tudo, sem nenhum botão de gravar.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { abrirComprovante, ArquivoRecusado, guardarArquivo } from '../dados/arquivos'
import { usarEstado } from '../dados/armazem'
import { usarCatira } from '../dados/catira'
import { usarExtrato } from '../dados/extrato'
import {
  anularNota, chaveDoPdf, descricaoDaNota, documentoDaNota, formatarChave, infoDaChave, lerCodigo, lerNotas, lerXmlDaNota,
  NOME_DA_ORIGEM, notaComAChave, notasDoPeriodo, notasLigadas, notaValendo, registrarNota, sentidoSugerido, totaisDoPeriodo,
  usarNotas, XmlRecusado, type LeituraDaNota, type Ligacao, type Nota, type Sentido,
} from '../dados/notas'
import { usarSessao, type Eu } from '../dados/sessao'
import { ferramentaLigada } from '../dominio/ferramentas'
import { pode } from '../dominio/permissoes'
import { agora, dataBR, hoje, lerNumero, novoId, num, quando, reais } from '../dominio/numeros'
import { avisar, Campo, Dica, Janela, pedirMotivo, Segmento, Tarja, type Ir } from '../ui'

/* ---------------------------------------------------------------------------
 * Datas do filtro
 * ------------------------------------------------------------------------- */

const f2 = (n: number) => String(n).padStart(2, '0')
const iso = (d: Date) => `${d.getFullYear()}-${f2(d.getMonth() + 1)}-${f2(d.getDate())}`

function mes(base: string, deslocar = 0): [string, string] {
  const [a, m] = base.split('-').map(Number) as [number, number]
  return [iso(new Date(a, m - 1 + deslocar, 1)), iso(new Date(a, m + deslocar, 0))]
}

/* ---------------------------------------------------------------------------
 * As ligações: o que existe neste aparelho e o nome de cada uma
 * ------------------------------------------------------------------------- */

interface Opcao { ligacao: Ligacao; rotulo: string; sentido: Sentido }

/** Fichas, despesas e movimentos da catira em que esta pessoa pode pendurar nota. */
function usarOpcoesDeLigacao(papel: string): { opcoes: Opcao[]; rotular: (l: Ligacao) => string } {
  const { fichas } = usarEstado()
  const ext = usarExtrato()
  const cat = usarCatira()
  return useMemo(() => {
    const opcoes: Opcao[] = []
    for (const f of [...fichas].sort((a, b) => b.data.localeCompare(a.data))) {
      if (f.situacao === 'excluida') continue
      opcoes.push({ ligacao: { tipo: 'ficha', id: f.id }, rotulo: `Ficha ${f.numero} · ${f.produtor.nome || 'sem produtor'} · ${dataBR(f.data)}`,
        sentido: f.finalidade === 'Venda' ? 'saida' : 'entrada' })
    }
    for (const l of [...ext.lancamentos].sort((a, b) => b.data.localeCompare(a.data))) {
      if (l.tipo !== 'despesa' || l.anulacao || l.recusa) continue
      opcoes.push({ ligacao: { tipo: 'despesa', id: l.id },
        rotulo: `Despesa · ${l.categoria || 'despesa'} · ${dataBR(l.data)} · ${reais(l.valor)}${papel === 'comprador' ? '' : ` · ${l.compradorNome}`}`,
        sentido: 'entrada' })
    }
    // a catira é do gestor (o comprador nem a recebe no aparelho)
    if (papel !== 'comprador') {
      for (const m of [...cat.movimentos].sort((a, b) => b.data.localeCompare(a.data))) {
        if ((m.tipo !== 'venda' && m.tipo !== 'recria') || m.anulacao || m.recusa) continue
        opcoes.push({ ligacao: { tipo: 'catira', id: m.id },
          rotulo: `Meu estoque · ${m.tipo === 'venda' ? 'venda' : `recria${m.categoria ? ` (${m.categoria})` : ''}`} · ${dataBR(m.data)} · ${reais(m.valor)}`,
          sentido: m.tipo === 'venda' ? 'saida' : 'entrada' })
      }
    }
    const porChave = new Map(opcoes.map((o) => [`${o.ligacao.tipo}:${o.ligacao.id}`, o.rotulo]))
    // ligação que não está neste aparelho (ex.: despesa anulada, ficha de outro): diz o tipo
    const rotular = (l: Ligacao) => porChave.get(`${l.tipo}:${l.id}`)
      ?? (l.tipo === 'ficha' ? 'Ficha' : l.tipo === 'despesa' ? 'Despesa do extrato' : 'Meu estoque')
    return { opcoes, rotular }
  }, [fichas, ext.lancamentos, cat.movimentos, papel])
}

function irPara(l: Ligacao, ir?: Ir): (() => void) | undefined {
  if (!ir) return undefined
  if (l.tipo === 'ficha') return () => ir({ tela: 'ficha', id: l.id, aba: 'pagamento' })
  if (l.tipo === 'despesa') return () => ir({ tela: 'extrato' })
  return () => ir({ tela: 'catira' })
}

/** Gestor anula qualquer nota; comprador, a que ele registrou; Consulta, nenhuma. */
function podeAnular(n: Nota, eu: Eu): boolean {
  if (n.anulacao || n.recusa) return false
  // 3E: a matriz única — o comprador só a que ele registrou
  return pode(eu.pessoa.papel, 'anularNota', { seu: n.lancadoPor === eu.pessoa.id })
}

function anular(n: Nota, eu: Eu): void {
  const motivo = pedirMotivo(`Anular a nota de ${reais(n.valor)} (${dataBR(n.data)})?\n\nEla continua na lista, riscada, com o motivo. Qual o motivo?`, true)
  if (motivo === null) return
  anularNota(n.id, motivo, eu.pessoa.id)
  avisar('Nota anulada — vai no próximo Sincronizar')
}

/* ---------------------------------------------------------------------------
 * A tela
 * ------------------------------------------------------------------------- */

export function Notas({ eu, ir }: { eu: Eu; ir: Ir }) {
  const nt = usarNotas()
  const papel = eu.pessoa.papel
  const { rotular } = usarOpcoesDeLigacao(papel)
  const [periodo, setPeriodo] = useState<[string, string]>(() => mes(hoje()))
  const [de, ate] = periodo
  const [sentido, setSentido] = useState<'todas' | Sentido>('todas')
  const [semLigacao, setSemLigacao] = useState(false)
  const [entrando, setEntrando] = useState(false)
  const pendentes = new Set(nt.pendentes)

  const filtro = { sentido: sentido === 'todas' ? null : sentido, semLigacao }
  const linhas = notasDoPeriodo(nt.notas, de, ate, filtro, rotular)
  const totais = totaisDoPeriodo(linhas)
  // as anuladas e recusadas do período também aparecem (riscadas): nada some
  const foraDaConta = nt.notas.filter((n) => !notaValendo(n) && n.data >= de && n.data <= ate
    && (!filtro.sentido || n.sentido === filtro.sentido) && (!semLigacao || !n.ligacao))
  const porId = new Map(nt.notas.map((n) => [n.id, n]))
  const lista = [...linhas.map((l) => porId.get(l.id)!), ...foraDaConta]
    .sort((a, b) => b.data.localeCompare(a.data) || b.lancadoEm.localeCompare(a.lancadoEm))
  const semLigacaoTotal = nt.notas.filter((n) => notaValendo(n) && !n.ligacao).length

  return (
    <div>
      <h2 className="secao">Notas fiscais</h2>
      <Dica>O app <b>registra</b> as notas, não emite. Leia o código da nota com a câmera, abra o XML ou o PDF, ou tire uma foto.
        {papel === 'comprador' ? ' Ligue cada nota a uma ficha sua ou a uma despesa do seu extrato.' : ''}
        {papel === 'consulta' ? ' Você só vê: quem registra é o gestor ou o comprador.' : ''}</Dica>

      {pode(papel, 'nota') && <button className="botao grande" onClick={() => setEntrando(true)}>+ Registrar nota</button>}

      {semLigacaoTotal > 0 && !semLigacao && (
        <Tarja onClick={() => setSemLigacao(true)}>
          {semLigacaoTotal === 1 ? 'Uma nota está' : `${semLigacaoTotal} notas estão`} sem ligação (nem ficha, nem despesa, nem Meu estoque). Toque para ver.
        </Tarja>
      )}

      <div className="cartao">
        <div className="linha">
          <Campo rotulo="De" tipo="date" valor={de} mudar={(v) => v && setPeriodo([v, ate])} />
          <Campo rotulo="Até" tipo="date" valor={ate} mudar={(v) => v && setPeriodo([de, v])} />
        </div>
        <div className="linha" style={{ marginTop: 8 }}>
          <button className="mini" onClick={() => setPeriodo(mes(hoje()))}>Este mês</button>
          <button className="mini" onClick={() => setPeriodo(mes(hoje(), -1))}>Mês passado</button>
          <button className="mini" onClick={() => setPeriodo([`${hoje().slice(0, 4)}-01-01`, `${hoje().slice(0, 4)}-12-31`])}>Este ano</button>
        </div>
        <div style={{ marginTop: 8 }}>
          <Segmento<'todas' | Sentido> valor={sentido} mudar={setSentido}
            opcoes={[['todas', 'Todas'], ['entrada', 'Entradas'], ['saida', 'Saídas']]} />
        </div>
        <label className="tl" style={{ marginTop: 8, alignItems: 'center' }}>
          <span><input type="checkbox" checked={semLigacao} onChange={(e) => setSemLigacao(e.target.checked)} /> Só as sem ligação</span>
        </label>
      </div>

      <div className="resultado">
        <div className="tl"><span>Entradas (compras, despesas)</span><b>{reais(totais.entradas)}</b></div>
        <div className="tl"><span>Saídas (vendas)</span><b>{reais(totais.saidas)}</b></div>
        <div className="mini-dica">{num(totais.quantas)} nota{totais.quantas === 1 ? '' : 's'} valendo de {dataBR(de)} a {dataBR(ate)}. Anuladas ficam fora da soma.</div>
      </div>

      <h2 className="secao">Notas de {dataBR(de)} a {dataBR(ate)}</h2>
      {lista.length
        ? lista.map((n) => (
          <LinhaDaNota key={n.id} n={n} eu={eu} pendente={pendentes.has(n.id)} ligadoA={n.ligacao ? rotular(n.ligacao) : ''}
            irLigacao={n.ligacao ? irPara(n.ligacao, ir) : undefined} />
        ))
        : <div className="cartao vazio">Nenhuma nota neste período{semLigacao ? ' sem ligação' : ''}.</div>}

      {/* nota de outro mês: o período pula para o mês dela, senão ela «some» da lista */}
      {entrando && <EntradaDeNota eu={eu} fechar={() => setEntrando(false)}
        salvou={(d) => { if (d < de || d > ate) setPeriodo(mes(d)) }} />}
    </div>
  )
}

/* ---------------------------------------------------------------------------
 * Uma nota na lista
 * ------------------------------------------------------------------------- */

function LinhaDaNota({ n, eu, pendente, ligadoA, irLigacao, compacto }: {
  n: Nota; eu: Eu; pendente: boolean; ligadoA: string; irLigacao?: () => void; compacto?: boolean
}) {
  const riscada = !!(n.anulacao || n.recusa)
  const titulo = descricaoDaNota(n) || documentoDaNota(n)
  return (
    <div className={compacto ? 'item' : 'cartao'} style={riscada ? { opacity: 0.6 } : undefined}>
      <div className="tl">
        <span><b>{titulo}</b>{compacto ? '' : ` · ${dataBR(n.data)}`}</span>
        <b style={riscada ? { textDecoration: 'line-through' } : undefined}>{reais(n.valor)}</b>
      </div>
      <div className="mini-dica">
        {compacto ? `${dataBR(n.data)} · ` : ''}{documentoDaNota(n)} · {n.sentido === 'entrada' ? 'entrada' : 'saída'} · por {NOME_DA_ORIGEM[n.origem]}
        {pendente && <span className="etiqueta aberta">a enviar</span>}
        {n.anulacao && <span className="etiqueta cancelada">anulada</span>}
        {n.recusa && <span className="etiqueta cancelada">recusada pelo servidor</span>}
      </div>
      {n.chave && <div className="mini-dica" style={{ fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>Chave {formatarChave(n.chave)}</div>}
      {!compacto && (n.emitenteNome || n.destinatarioNome) && (
        <div className="mini-dica">
          {n.emitenteNome && <>Emitente: {n.emitenteNome}{n.emitenteDoc ? ` (${n.emitenteDoc})` : ''}</>}
          {n.emitenteNome && n.destinatarioNome && ' · '}
          {n.destinatarioNome && <>Para: {n.destinatarioNome}</>}
        </div>
      )}
      {!compacto && n.itens.length > 0 && (
        <details className="mini-dica">
          <summary>{n.itens.length} {n.itens.length === 1 ? 'item' : 'itens'}</summary>
          {n.itens.map((i, k) => (
            <div key={k}>{i.descricao}{i.quantidade !== null ? ` · ${num(i.quantidade, i.quantidade % 1 ? 2 : 0)} ${i.unidade}` : ''}{i.valor !== null ? ` · ${reais(i.valor)}` : ''}</div>
          ))}
        </details>
      )}
      {!compacto && (
        <div className="mini-dica">
          {n.ligacao
            ? <>Ligada a: {irLigacao ? <span className="clicavel" style={{ textDecoration: 'underline' }} onClick={irLigacao}>{ligadoA}</span> : ligadoA}</>
            : 'Sem ligação'}
          {' · registrada por '}{n.lancadoPorNome || (n.lancadoPor === eu.pessoa.id ? eu.pessoa.nome : '—')}
        </div>
      )}
      {n.anulacao && <div className="mini-dica">Motivo da anulação: {n.anulacao.motivo} · {quando(n.anulacao.em)}</div>}
      {n.recusa && <div className="mini-dica">Não entrou no banco e fica fora das listas: {n.recusa.motivo} Fica só neste aparelho.</div>}
      {(n.arquivoId || n.urlConsulta || podeAnular(n, eu)) && (
        <div className="linha" style={{ marginTop: 6 }}>
          {n.arquivoId && (
            <button className="mini" onClick={async () => {
              const erro = await abrirComprovante(n.arquivoId!)
              if (erro) window.alert(erro)
            }}>📎 Ver arquivo</button>
          )}
          {n.urlConsulta && !compacto && (
            <a className="mini" href={n.urlConsulta} target="_blank" rel="noopener noreferrer">Consultar na SEFAZ</a>
          )}
          {podeAnular(n, eu) && <button className="mini" onClick={() => anular(n, eu)}>Anular</button>}
        </div>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------------------
 * Na ficha, na despesa e no movimento da catira: as notas ligadas e o «+ Nota»
 * ------------------------------------------------------------------------- */

/**
 * As notas ligadas a um registro, com o botão «+ Nota». `compacto`: dentro do
 * cartão da despesa ou do movimento, sem título. `podeAdicionar` = o registro
 * ainda vale (não anulado); a Consulta nunca vê o botão.
 */
export function NotasLigadas({ ligacao, compacto, podeAdicionar = true }: { ligacao: Ligacao; compacto?: boolean; podeAdicionar?: boolean }) {
  const sessao = usarSessao()
  const nt = usarNotas()
  const [entrando, setEntrando] = useState(false)
  if (sessao.tipo !== 'dentro') return null
  // 3C: «Notas fiscais» desligada em Ajustes › Ferramentas esconde o bloco e o
  // «+ Nota» em todo lugar (ficha, despesa, catira); as notas continuam guardadas
  if (!ferramentaLigada(sessao.eu.conta, 'notas')) return null
  const eu = sessao.eu
  const ligadas = notasLigadas(nt.notas, ligacao).sort((a, b) => b.data.localeCompare(a.data))
  const pendentes = new Set(nt.pendentes)
  const botao = pode(eu.pessoa.papel, 'nota') && podeAdicionar
  if (compacto && !ligadas.length && !botao) return null
  return (
    <>
      {!compacto && <h2 className="secao">Notas fiscais</h2>}
      <div className={compacto ? '' : 'cartao'} style={compacto ? { marginTop: 6 } : undefined}>
        {ligadas.map((n) => <LinhaDaNota key={n.id} n={n} eu={eu} pendente={pendentes.has(n.id)} ligadoA="" compacto />)}
        {!compacto && !ligadas.length && <div className="mini-dica" style={{ marginTop: 0 }}>Nenhuma nota ligada ainda.</div>}
        {botao && (
          <button className={compacto ? 'mini' : 'botao fantasma pequeno'} onClick={() => setEntrando(true)}>+ Nota</button>
        )}
      </div>
      {entrando && <EntradaDeNota eu={eu} ligacao={ligacao} fechar={() => setEntrando(false)} />}
    </>
  )
}

/* ---------------------------------------------------------------------------
 * Registrar: escolher o caminho (câmera, arquivo, foto) e depois conferir
 * ------------------------------------------------------------------------- */

interface DetectorDeCodigo { detect(fonte: ImageBitmapSource): Promise<{ rawValue: string; format: string }[]> }
interface ClasseDoDetector {
  new (opcoes: { formats: string[] }): DetectorDeCodigo
  getSupportedFormats?(): Promise<string[]>
}
/** A leitora de código do navegador (Android/Chrome tem; o iPhone não). */
function detectorDoNavegador(): ClasseDoDetector | null {
  return (globalThis as unknown as { BarcodeDetector?: ClasseDoDetector }).BarcodeDetector ?? null
}

async function criarDetector(): Promise<DetectorDeCodigo | null> {
  const Classe = detectorDoNavegador()
  if (!Classe) return null
  // code_128 é o código de barras da DANFE; qr_code, o do cupom
  let formatos = ['code_128', 'qr_code']
  try {
    const tem = await Classe.getSupportedFormats?.()
    if (tem) formatos = formatos.filter((f) => tem.includes(f))
  } catch { /* fica com os dois */ }
  if (!formatos.length) return null
  try { return new Classe({ formats: formatos }) } catch { return null }
}

/** Tenta ler um código numa foto já tirada (quando a câmera ao vivo não abre). */
async function lerCodigoDaFoto(arquivo: Blob): Promise<LeituraDaNota | null> {
  const detector = await criarDetector()
  if (!detector || typeof createImageBitmap !== 'function') return null
  try {
    const bmp = await createImageBitmap(arquivo)
    try {
      for (const c of await detector.detect(bmp)) {
        const l = lerCodigo(c.rawValue)
        if (l) return l
      }
    } finally { bmp.close() }
  } catch { /* foto que não se lê: segue sem chave */ }
  return null
}

const ehPdf = (f: File) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name)
const ehXml = (f: File) => f.type === 'application/xml' || f.type === 'text/xml' || /\.xml$/i.test(f.name)

interface Rascunho { leitura: LeituraDaNota; arquivo: Blob | null; nomeDoArquivo: string; aviso?: string }

function EntradaDeNota({ eu, ligacao, fechar, salvou }: { eu: Eu; ligacao?: Ligacao; fechar: () => void; salvou?: (data: string) => void }) {
  const [rascunho, setRascunho] = useState<Rascunho | null>(null)
  const [camera, setCamera] = useState(false)
  const [lendo, setLendo] = useState(false)
  const temLeitora = !!detectorDoNavegador()
  const arquivo = useRef<HTMLInputElement>(null)
  const foto = useRef<HTMLInputElement>(null)
  const galeria = useRef<HTMLInputElement>(null)

  async function abrirArquivo(f: File | undefined) {
    if (!f) return
    setLendo(true)
    try {
      if (ehXml(f)) {
        const texto = await f.text()
        const leitura = lerXmlDaNota(texto)
        setRascunho({ leitura, arquivo: new Blob([texto], { type: 'application/xml' }), nomeDoArquivo: f.name || 'nota.xml' })
      } else if (ehPdf(f)) {
        const chave = await chaveDoPdf(new Uint8Array(await f.arrayBuffer()))
        setRascunho({ leitura: { origem: 'pdf', chave }, arquivo: f, nomeDoArquivo: f.name || 'nota.pdf',
          aviso: chave ? undefined : 'Não achei a chave neste PDF (ele pode ser só imagem). O arquivo fica guardado; digite o valor e a data.' })
      } else {
        window.alert('Escolha o arquivo XML ou o PDF da nota. Para foto, use «Foto ou print».')
      }
    } catch (e) {
      window.alert(e instanceof XmlRecusado ? e.message : 'Não deu para ler este arquivo.')
    } finally { setLendo(false) }
  }

  async function abrirFoto(f: File | undefined) {
    if (!f) return
    setLendo(true)
    // foto não tem leitura automática; se der para achar o código nela, melhor
    const achou = await lerCodigoDaFoto(f)
    setLendo(false)
    setRascunho({ leitura: { origem: 'foto', chave: achou?.chave ?? null, urlConsulta: achou?.urlConsulta }, arquivo: f, nomeDoArquivo: f.name || 'foto da nota.jpg' })
  }

  if (rascunho) return <FormDaNota eu={eu} rascunho={rascunho} ligacao={ligacao} fechar={fechar} salvou={salvou} />
  if (camera) {
    return <LeitorDeCodigo fechar={() => setCamera(false)}
      lido={(leitura) => { setCamera(false); setRascunho({ leitura, arquivo: null, nomeDoArquivo: '' }) }} />
  }
  return (
    <Janela titulo="Registrar nota" fechar={fechar}>
      {temLeitora
        ? <button className="botao grande" onClick={() => setCamera(true)}>📷 Ler o código da nota com a câmera</button>
        // R2B: no computador o aviso não faz sentido (ninguém lê código de barras pela câmera do computador)
        : celular() && <Tarja>Este celular não lê código de barras pelo app (o iPhone, por exemplo). Use o XML ou o PDF da nota, ou tire uma foto.</Tarja>}
      {temLeitora && <div className="mini-dica">Código de barras da DANFE ou QR do cupom (almoço, combustível, borracharia). Funciona sem sinal.</div>}
      <button className="botao" disabled={lendo} onClick={() => arquivo.current?.click()}>📄 Arquivo XML ou PDF da nota</button>
      <div className="mini-dica">O XML (chega por e-mail) traz tudo: quem emitiu, valor, data e itens. Do PDF, o app tira a chave.</div>
      <button className="botao fantasma" disabled={lendo} onClick={() => foto.current?.click()}>🖼️ Tirar foto da nota</button>
      <button className="botao fantasma pequeno" disabled={lendo} onClick={() => galeria.current?.click()}>ou escolher uma foto ou print já salvo</button>
      <div className="mini-dica">Foto: o app guarda a imagem e você digita o valor e a data.</div>
      {lendo && <div className="mini-dica">Lendo…</div>}
      <input ref={arquivo} type="file" accept=".xml,.pdf,application/xml,text/xml,application/pdf" hidden
        onChange={(e) => { void abrirArquivo(e.target.files?.[0]); e.target.value = '' }} />
      <input ref={foto} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { void abrirFoto(e.target.files?.[0]); e.target.value = '' }} />
      <input ref={galeria} type="file" accept="image/*" hidden onChange={(e) => { void abrirFoto(e.target.files?.[0]); e.target.value = '' }} />
      <button className="botao fantasma" onClick={fechar}>Cancelar</button>
    </Janela>
  )
}

/**
 * A câmera ao vivo procurando o código. Se a câmera não abrir (sem permissão),
 * oferece tirar uma foto do código e lê nela.
 */
function LeitorDeCodigo({ lido, fechar }: { lido: (l: LeituraDaNota) => void; fechar: () => void }) {
  const video = useRef<HTMLVideoElement>(null)
  const foto = useRef<HTMLInputElement>(null)
  const [situacao, setSituacao] = useState<'abrindo' | 'procurando' | 'sem_camera'>('abrindo')
  const [estranho, setEstranho] = useState(false)

  useEffect(() => {
    let vivo = true
    let fluxo: MediaStream | null = null
    let espera: ReturnType<typeof setTimeout> | undefined
    ;(async () => {
      const detector = await criarDetector()
      if (!detector || !navigator.mediaDevices?.getUserMedia) { if (vivo) setSituacao('sem_camera'); return }
      try {
        fluxo = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      } catch {
        if (vivo) setSituacao('sem_camera')
        return
      }
      if (!vivo || !video.current) { fluxo.getTracks().forEach((t) => t.stop()); return }
      video.current.srcObject = fluxo
      await video.current.play().catch(() => undefined)
      setSituacao('procurando')
      const procurar = async () => {
        if (!vivo || !video.current) return
        try {
          for (const c of await detector.detect(video.current)) {
            const l = lerCodigo(c.rawValue)
            if (l) { vivo = false; lido(l); return }
            setEstranho(true)
          }
        } catch { /* quadro ainda sem imagem: tenta no próximo */ }
        espera = setTimeout(procurar, 300)
      }
      void procurar()
    })()
    return () => {
      vivo = false
      clearTimeout(espera)
      fluxo?.getTracks().forEach((t) => t.stop())
    }
  }, [])

  async function daFoto(f: File | undefined) {
    if (!f) return
    const l = await lerCodigoDaFoto(f)
    if (l) lido(l)
    else window.alert('Não achei o código nesta foto. Chegue mais perto, com luz, e com o código inteiro na foto.')
  }

  return (
    <Janela titulo="Ler o código da nota" fechar={fechar}>
      {situacao !== 'sem_camera' && (
        <video ref={video} playsInline muted style={{ width: '100%', maxHeight: 320, objectFit: 'cover', borderRadius: 10, background: '#000' }} />
      )}
      {situacao === 'abrindo' && <div className="mini-dica">Abrindo a câmera…</div>}
      {situacao === 'procurando' && <div className="mini-dica">Aponte para o código de barras da DANFE ou para o QR do cupom. Lê sozinho.</div>}
      {estranho && <div className="mini-dica">Este código não é de nota fiscal. Procure o código grande da DANFE ou o QR do cupom.</div>}
      {situacao === 'sem_camera' && (
        <>
          <Tarja>A câmera não abriu aqui (sem permissão, ou o celular não deixa). Tire uma foto do código: o app lê nela.</Tarja>
          <button className="botao" onClick={() => foto.current?.click()}>📷 Tirar foto do código</button>
          <input ref={foto} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { void daFoto(e.target.files?.[0]); e.target.value = '' }} />
        </>
      )}
      <button className="botao fantasma" onClick={fechar}>Voltar</button>
    </Janela>
  )
}

/** Conferir o que foi lido, completar à mão e guardar. */
function FormDaNota({ eu, rascunho, ligacao, fechar, salvou }: {
  eu: Eu; rascunho: Rascunho; ligacao?: Ligacao; fechar: () => void; salvou?: (data: string) => void
}) {
  const papel = eu.pessoa.papel
  const { opcoes } = usarOpcoesDeLigacao(papel)
  const l = rascunho.leitura
  const info = l.chave ? infoDaChave(l.chave) : null
  const opcaoInicial = ligacao ? opcoes.find((o) => o.ligacao.tipo === ligacao.tipo && o.ligacao.id === ligacao.id) : undefined
  const [lig, setLig] = useState(ligacao ? `${ligacao.tipo}:${ligacao.id}` : '')
  const [sentido, setSentido] = useState<Sentido>(() =>
    l.tipoNoXml ? sentidoSugerido(l, eu.conta.doc) : opcaoInicial?.sentido ?? 'entrada')
  const [data, setData] = useState(l.data ?? hoje())
  const [valor, setValor] = useState(l.valor !== undefined ? String(l.valor).replace('.', ',') : '')
  const [descricao, setDescricao] = useState('')
  const [extra, setExtra] = useState<File | null>(null)
  const [indo, setIndo] = useState(false)
  const anexo = useRef<HTMLInputElement>(null)
  const repetida = notaComAChave(lerNotas().notas, l.chave)

  function escolherLigacao(v: string) {
    setLig(v)
    // a ligação sugere o sentido (venda é saída); o XML, quando há, manda mais
    const o = opcoes.find((x) => `${x.ligacao.tipo}:${x.ligacao.id}` === v)
    if (o && !l.tipoNoXml) setSentido(o.sentido)
  }

  async function salvar() {
    if (repetida) { window.alert(`Esta nota já está registrada (${dataBR(repetida.data)}, ${reais(repetida.valor)}). A mesma nota não entra duas vezes.`); return }
    const v = lerNumero(valor)
    if (v === null || v < 0) { window.alert('Informe o valor da nota.'); return }
    if (!data) { window.alert('Informe a data da nota.'); return }
    const [tipo, ...resto] = lig.split(':')
    const ligacaoEscolhida: Ligacao | null = lig ? { tipo: tipo as Ligacao['tipo'], id: resto.join(':') } : null
    if (papel === 'comprador' && (!ligacaoEscolhida || ligacaoEscolhida.tipo === 'catira')) {
      window.alert('Escolha a ficha ou a despesa desta nota.'); return
    }
    setIndo(true)
    try {
      const blob = rascunho.arquivo ?? extra
      const nome = rascunho.arquivo ? rascunho.nomeDoArquivo : extra?.name ?? ''
      let arquivoId: string | null = null
      let arquivo: Nota['arquivo'] = null
      if (blob) {
        arquivoId = novoId()
        // a foto é guardada já comprimida: tipo e tamanho são os do guardado
        const guardado = await guardarArquivo(arquivoId, blob)
        arquivo = { nome: nome || 'arquivo da nota', tipo: guardado.type, tamanho: guardado.size }
      }
      registrarNota({
        id: novoId(), chave: l.chave, origem: l.origem, sentido, data, valor: Math.round(v * 100) / 100,
        numero: l.numero ?? info?.numero ?? '', serie: l.serie ?? info?.serie ?? '',
        emitenteDoc: l.emitenteDoc ?? info?.emitenteDoc ?? '', emitenteNome: l.emitenteNome ?? '',
        destinatarioDoc: l.destinatarioDoc ?? '', destinatarioNome: l.destinatarioNome ?? '',
        descricao: descricao.trim(), itens: l.itens ?? [], urlConsulta: l.urlConsulta ?? '',
        arquivoId, arquivo, ligacao: ligacaoEscolhida,
        lancadoPor: eu.pessoa.id, lancadoPorNome: eu.pessoa.nome, lancadoEm: agora(), anulacao: null,
      })
      avisar('Nota guardada — vai no próximo Sincronizar')
      salvou?.(data)
      fechar()
    } catch (e) {
      window.alert(e instanceof ArquivoRecusado ? e.message : 'Não foi possível guardar o arquivo neste aparelho.')
    } finally { setIndo(false) }
  }

  const tipos: [Ligacao['tipo'], string][] = [['ficha', 'Fichas'], ['despesa', 'Despesas do extrato'], ['catira', 'Meu estoque']]
  return (
    <Janela titulo={`Nota lida por ${NOME_DA_ORIGEM[l.origem]}`} fechar={fechar}>
      {rascunho.aviso && <Tarja>{rascunho.aviso}</Tarja>}
      {repetida && <Tarja tipo="erro">Esta nota já está registrada ({dataBR(repetida.data)}, {reais(repetida.valor)}). A mesma nota não entra duas vezes.</Tarja>}
      {l.chave
        ? <div className="cartao" style={{ marginTop: 0 }}>
            <div className="mini-dica" style={{ marginTop: 0 }}>{documentoDaNota({ chave: l.chave, numero: l.numero ?? '', serie: l.serie ?? '', origem: l.origem })}
              {info ? ` · emitida em ${info.anoMes.split('-').reverse().join('/')}` : ''}</div>
            <div style={{ fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}><b>{formatarChave(l.chave)}</b></div>
            {l.emitenteNome && <div className="mini-dica">Emitente: {l.emitenteNome}{l.emitenteDoc ? ` (${l.emitenteDoc})` : ''}</div>}
            {l.destinatarioNome && <div className="mini-dica">Para: {l.destinatarioNome}</div>}
            {!!l.itens?.length && <div className="mini-dica">{l.itens.length} {l.itens.length === 1 ? 'item' : 'itens'}: {l.itens.slice(0, 3).map((i) => i.descricao).join('; ')}{l.itens.length > 3 ? '…' : ''}</div>}
            {(l.origem === 'barras' || l.origem === 'qr') && l.valor === undefined && (
              <div className="mini-dica">A câmera lê só a chave: confira o valor e a data no papel.</div>
            )}
          </div>
        : rascunho.arquivo && <div className="mini-dica">📎 {rascunho.nomeDoArquivo}</div>}

      <div className="campo"><span>Esta nota é</span></div>
      <Segmento<Sentido> valor={sentido} mudar={setSentido} opcoes={[['entrada', 'Entrada (compra, despesa)'], ['saida', 'Saída (venda)']]} />
      <label className="campo"><span>Valor total (R$)</span>
        <input inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="ex.: 1.250,00" /></label>
      <Campo rotulo="Data da nota" tipo="date" valor={data} mudar={setData} />

      {ligacao
        ? <div className="mini-dica">Ligada a: {opcaoInicial?.rotulo ?? 'este registro'}</div>
        : (
          <label className="campo"><span>Ligar a</span>
            <select value={lig} onChange={(e) => escolherLigacao(e.target.value)}>
              <option value="">{papel === 'comprador' ? '— escolha a ficha ou a despesa —' : 'Sem ligação (por enquanto)'}</option>
              {tipos.map(([t, nome]) => {
                const doTipo = opcoes.filter((o) => o.ligacao.tipo === t)
                return doTipo.length ? (
                  <optgroup key={t} label={nome}>
                    {doTipo.map((o) => <option key={o.ligacao.id} value={`${t}:${o.ligacao.id}`}>{o.rotulo}</option>)}
                  </optgroup>
                ) : null
              })}
            </select></label>
        )}
      {!ligacao && papel === 'comprador' && !opcoes.length && (
        <Dica>Você ainda não tem ficha nem despesa neste aparelho. Lance a despesa no Extrato (ou abra a ficha) e toque em «+ Nota» nela.</Dica>
      )}
      <Campo rotulo="Observação" valor={descricao} mudar={setDescricao} dica="ex.: diesel da viagem a Marabá" />

      {!rascunho.arquivo && (
        <>
          <button className="botao fantasma pequeno" onClick={() => anexo.current?.click()}>
            {extra ? `📎 ${extra.name}` : '📎 Anexar foto ou PDF da nota (opcional)'}</button>
          <input ref={anexo} type="file" accept="image/*,application/pdf" hidden onChange={(e) => { setExtra(e.target.files?.[0] ?? null); e.target.value = '' }} />
        </>
      )}
      <Dica>Registrar não é emitir: o app guarda a nota que alguém emitiu. Errou? Anule com motivo e registre de novo.</Dica>
      <button className="botao grande" disabled={indo || !!repetida} onClick={salvar}>{indo ? 'Guardando…' : 'Guardar nota'}</button>
      <button className="botao fantasma" onClick={fechar}>Cancelar</button>
    </Janela>
  )
}

/** R2B · Está num celular (toque e tela estreita)? No computador, o aviso da câmera some. */
function celular(): boolean {
  try {
    return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) || (window.matchMedia?.('(pointer: coarse)').matches ?? false)
  } catch { return false }
}
